import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import FormBuilder from "../../../../../../shared/components/form-builder/form-builder";
import { TcComponentInformation } from "../../../../tc-models/tc-req-dto/input-tc-component-details.res_dto";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { Option } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { useParams } from "react-router-dom";
import { Utills } from "../../../../../../shared/app-configs/utilit";

interface ProductInfoProps {
  formRef: any;
  componentData: TcComponentInformation;
  setComponentData: React.Dispatch<
    React.SetStateAction<TcComponentInformation>
  >;
  setSelectedComponentDetails: React.Dispatch<
    React.SetStateAction<TcComponentInformation>
  >;
}

const ComponentDetailsForm: React.FC<ProductInfoProps> = ({
  formRef,
  componentData,
  setComponentData,
  setSelectedComponentDetails,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { TE_ID } = useParams();
  const { executeGet } = useApi();
  const [productNoiOptions, setProductNoOptions] = useState<Option[]>([]);
  const [productDetails, setProductDetails] = useState<any[]>([]);
  const [inputTcId, setInputTcId] = useState<string>();
  const [selectedComponent, setSelectedComponent] = useState<any>();
  const [componentList, setComponentList] = useState<any[]>([]);
  const [componentNoOptions, setComponentNoOptions] = useState<any[]>([]);
  const toggleDrawer = () => {
    if (drawerOpen) resetForm();
    setDrawerOpen((prev) => !prev);
  };

  const resetForm = () => {
    setComponentData(componentData);
  };
  const loadFormData = async () => {
    try {
      const response: any = await executeGet(
        TCAppConfig.GET_INPUT_TC_PRODUCTDETAILS_INFO,
        { tcId: TE_ID }
      );
      setProductDetails(response?.data.response || []);
      const result =
        response?.data.response?.map((detail: any) => ({
          value: detail.inputTcProductNo ?? "",
          label: detail.inputTcProductNo
            ? `${detail?.inputTcNo}/${detail.inputTcProductNo}`
            : "",
        })) || [];
      setProductNoOptions(result);
    } catch (err) {
      console.error("Failed to fetch TC Base Details:", err);
    }
  };

  // When editing, if initial product no exists, pre-select it and load components
  useEffect(() => {
    if (!productDetails || productDetails.length === 0) return;
    const initialProductNo = (componentData as any)?.inputTcProductNo;
    if (initialProductNo) {
      const chosenPdct = productDetails.find(
        (pdctDetail) => pdctDetail?.inputTcProductNo === initialProductNo
      );
      if (chosenPdct) {
        formRef?.current?.setFieldValue("inputTcProductNo", initialProductNo);
        setInputTcId(chosenPdct?.selectedInputTcId);
        loadComponentData(chosenPdct?.selectedInputTcId);
      }
    }
    // eslint-disable-next-line
  }, [productDetails]);

  // After components load, if initial component no exists, pre-select it and load details
  useEffect(() => {
    if (!componentList || componentList.length === 0) return;
    const initialComponentNo = (componentData as any)?.inputTcProductComponentNo;
    if (initialComponentNo) {
      const comp = componentList.find(
        (c) => c.tcProductComponentNo === initialComponentNo || c.componentId === initialComponentNo
      );
      if (comp) {
        formRef?.current?.setFieldValue(
          "inputTcProductComponentNo",
          comp.componentId
        );
        loadComponentDetailsData(comp);
      }
    }
    // eslint-disable-next-line
  }, [componentList]);

  const loadComponentData = async (tcId: string) => {
    if (!tcId || tcId.trim() === "") return;
    try {
      const response: any = await executeGet(
        TCAppConfig.GET_COMPONENT_DETAILS_LIST,
        { tcId }
      );
      setComponentList(response?.data.response || []);
      const result =
        response?.data.response?.map((detail: any) => ({
          value: detail.componentId ?? "",
          label: detail.tcProductComponentNo ?? "",
        })) || [];
      setComponentNoOptions(result);
    } catch (err) {
      console.error("Failed to fetch TC Component Details:", err);
    }
  };

  const loadComponentDetailsData = async (component: any) => {
    if (!component) return;
    try {
      const response: any = await executeGet(
        TCAppConfig.GET_COMPONENT_DETAILS_NEW,
        {
          tcId: component?.tcId,
          shipmentId: component?.shipmentId,
          productId: component?.productId,
          componentId: component?.componentId,
        }
      );
      const componentDetails = response?.data?.tcComponentInformation[0];
      formRef?.current?.setFieldValue(
        "inputTcComponentDetail",
        componentDetails?.tcComponentDetail
      );
      formRef?.current?.setFieldValue(
        "inputTcComponentInfoTrimsExcluded",
        componentDetails?.tcComponentInfoTrimsExcluded
      );
      formRef?.current?.setFieldValue(
        "inputTcComponentInfoAssociatedTc",
        componentDetails?.tcComponentInfoAssociatedTc
      );
      formRef?.current?.setFieldValue(
        "inputTcComponentInfoFiberLength",
        componentDetails?.tcComponentInfoFiberLength
      );
      formRef?.current?.setFieldValue(
        "inputTcComponentInfoYarnCount",
        componentDetails?.tcComponentInfoYarnCount
      );
      formRef?.current?.setFieldValue(
        "inputTcComponentInfoFabricWeightAndConstruct",
        componentDetails?.tcComponentInfoFabricWeightAndConstruct
      );
      formRef?.current?.setFieldValue(
        "inputTcComponentInfoFabricWeightOfFinishedProduct",
        componentDetails?.tcComponentInfoFabricWeightOfFinishedProduct
      );
      formRef?.current?.setFieldValue(
        "inputTcComponentNetShippingWeight",
        componentDetails?.tcComponentNetShippingWeight
      );
      formRef?.current?.setFieldValue(
        "inputTcComponentSupplementaryWeight",
        componentDetails?.tcComponentSupplementaryWeight
      );
      formRef?.current?.setFieldValue(
        "inputTcComponentInfoAdditional",
        componentDetails?.tcComponentInfoAdditional
      );

      setSelectedComponent(componentDetails);
      setSelectedComponentDetails(componentDetails);
    } catch (err) {
      console.error("Failed to fetch TC Component Details:", err);
    }
  };
  useEffect(() => {
    TE_ID && loadFormData();
  }, []);
  const formConfig = {
    pageName: "input-tc-component-details",
    initialValues: componentData,
    validationSchema: Yup.object().shape({
      inputTcProductNo: Yup.string().required(
        "Please pick Input TC Product No."
      ),
      inputTcProductComponentNo: Yup.string().nullable(),
      inputTcComponentDetail: Yup.string().nullable(),
      inputTcComponentInfoTrimsExcluded: Yup.string().nullable(),
      inputTcComponentInfoAssociatedTc: Yup.string().nullable(),
      inputTcComponentInfoFiberLength: Yup.string().nullable(),
      inputTcComponentInfoYarnCount: Yup.string().nullable(),
      inputTcComponentInfoFabricWeightAndConstruct: Yup.string().nullable(),
      inputTcComponentInfoFabricWeightOfFinishedProduct:
        Yup.string().nullable(),
      inputTcComponentNetShippingWeight: Yup.string().test(
        "is-exactly-two-decimal-places",
        "Number must be a valid decimal number with upto 2 decimal places.",
        (value: string | undefined) => {
          if (!value) return true; // Allow empty values for required validation to handle
          return /^(0|[1-9]\d*|0\.\d{1,2}|[1-9]\d*\.\d{1,2})$/.test(value); // Match exactly two decimal places
        }
      ),
      inputTcComponentSupplementaryWeight: Yup.string().test(
        "is-exactly-two-decimal-places",
        "Number must be a valid decimal number with upto 2 decimal places.",
        (value: string | undefined) => {
          if (!value) return true; // Allow empty values for required validation to handle
          return /^(0|[1-9]\d*|0\.\d{1,2}|[1-9]\d*\.\d{1,2})$/.test(value); // Match exactly two decimal places
        }
      ),
      inputTcComponentInfoAdditional: Yup.string().nullable(),
    }),
    sections: [
      {
        title: "Input Product Component Details",
        fields: [
          {
            label: "Input Product No.",
            name: "inputTcProductNo",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            enableSearch: true,
            props: {
              options: productNoiOptions,
              placeholder: "Select Input TC Product No.",
              onChange: (e: any) => {
                const chosenPdct = productDetails?.find(
                  (pdctDetail) =>
                    pdctDetail?.inputTcProductNo === e.target.value
                );
                formRef.current.setFieldValue(
                  "inputTcProductNo",
                  e?.target?.value
                );
                setInputTcId(chosenPdct?.selectedInputTcId);
                loadComponentData(chosenPdct?.selectedInputTcId);
              },
            },
          },
          {
            label: "Product Component No.", //'Input Product Component No.',
            name: "inputTcProductComponentNo",
            type: "select",
            disabled: !componentNoOptions || componentNoOptions.length === 0,
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            props: {
              placeholder: "Select Product Component No.",
              options: componentNoOptions,
              onChange: (e: any) => {
                formRef.current.setFieldValue(
                  "inputTcProductComponentNo",
                  e?.target?.value
                );
                const comp = componentList?.find(
                  (c) => c.componentId === e?.target?.value
                );
                loadComponentDetailsData(comp);
              },
            },
          },
          {
            label: "Component Detail", //renamed to match tbl hdr
            name: "inputTcComponentDetail",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            props: {
              placeholder: "Enter Component Detail",
            },
          },
          {
            label: "Component Info Trims Excluded", //label: 'Input Component Info Trims',renamed to match tbl hdr
            name: "inputTcComponentInfoTrimsExcluded",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            props: { placeholder: "Enter Trims Excluded Info" },
          },
          {
            label: "Component Info Associated TC", //renamed to match tbl hdr label: 'Input Component Info Associated TC',
            name: "inputTcComponentInfoAssociatedTc",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            props: { placeholder: "Enter Associated TC Info" },
          },
          {
            label: "Component Info Fiber Length", //renamed to match tbl hdr label
            name: "inputTcComponentInfoFiberLength",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            props: { placeholder: "Enter Fiber Length" },
          },
          {
            label: "Product Component Info Yarn Count", //renamed to match tbl hdr
            name: "inputTcComponentInfoYarnCount",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            props: { placeholder: "Enter Yarn Count" },
          },
          {
            label: "Component Info Fabric Weight and Construct (kg)", //renamed to match tbl hdr label
            name: "inputTcComponentInfoFabricWeightAndConstruct",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            props: { placeholder: "Enter Fabric Weight and Construct" },
          },
          {
            label: "Component Info Fabric Weight of Finished Product (kg)", //renamed to match tbl hdr label
            name: "inputTcComponentInfoFabricWeightOfFinishedProduct",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            props: { placeholder: "Enter Fabric Weight of Finished Product" },
          },
          {
            label: "Component Net Shipping Weight (kg)", //renamed to match tbl hdr label
            name: "inputTcComponentNetShippingWeight",
            type: "number",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            props: { placeholder: "Enter Net Shipping Weight", type: "number" },
          },
          {
            label: "Component Supplementary Weight (kg)", //renamed to match tbl hdr label
            name: "inputTcComponentSupplementaryWeight",
            type: "number",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            props: {
              placeholder: "Enter Supplementary Weight",
              type: "number",
            },
          },
          {
            label: "Input Component Info Additional",
            name: "inputTcComponentInfoAdditional",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            props: { placeholder: "Enter Additional Info" },
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      Utills.cl("Form Submitted:", values);
      setComponentData(values);
      toggleDrawer();
    },
  };

  return (
    <>
      <FormBuilder
        formConfig={formConfig}
        onSubmit={formConfig.handleSubmit}
        formRef={formRef}
      />
    </>
  );
};

export default ComponentDetailsForm;
